import { useState } from 'react';
import { Nullable, StatusesAPI } from '@shared';
import { UseMutateFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { auditApi, auditor } from 'App';

export type useStatusesReturnType = {
  statuses: Nullable<StatusesAPI>;
  updateStatuses: UseMutateFunction<
    StatusesAPI,
    unknown,
    { auditUuid: string; statuses: Partial<StatusesAPI> }
  >;
  isStatusesLoading: boolean;
  refetchStatuses: (newStatuses?: useStatusesReturnType['statuses']) => void;
};

type NeedsReviewOldDataType = Nullable<auditor.NeedsReviewTableAPI>;

export const useStatuses = (auditUuid: string): useStatusesReturnType => {
  const {
    data,
    refetch,
    isLoading: isLoadingRefetch,
  } = useQuery<StatusesAPI>(['statuses', auditUuid], () => auditApi.getStatuses(auditUuid), {
    onSuccess: (data) => setStatuses(data),
  });
  const queryClient = useQueryClient();

  const { mutate: updateStatuses, isLoading: isLoadingMutation } = useMutation({
    mutationFn: ({
      auditUuid,
      statuses,
    }: {
      auditUuid: string;
      statuses: Partial<StatusesAPI>;
    }) => {
      return auditApi.updateStatuses(auditUuid, statuses);
    },
    onSuccess: (data, { auditUuid }) => {
      setStatuses(data);
      queryClient.setQueryData(['statuses', auditUuid], data);

      const currentPage = queryClient.getQueryData(['needs review current page']);
      queryClient.setQueryData(['needs review', currentPage], (oldData: NeedsReviewOldDataType) => {
        if (!oldData) return;
        const { compositeAudits, totalResults } = oldData;
        if (data.needsReview) {
          queryClient.refetchQueries(['needs review', currentPage], { stale: false });
        }
        if (!data.needsReview) {
          const filteredAudits = compositeAudits.filter(({ audit }) => audit.uuid !== auditUuid);
          return {
            compositeAudits: filteredAudits,
            totalResults: totalResults - 1,
          };
        }
      });
    },
  });

  const isStatusesLoading = isLoadingRefetch || isLoadingMutation;

  const [statuses, setStatuses] = useState<useStatusesReturnType['statuses']>(data);

  const refetchStatuses = (newStatuses?: useStatusesReturnType['statuses']) => {
    if (newStatuses) {
      setStatuses(newStatuses);
    } else {
      refetch();
    }
  };

  return { statuses, refetchStatuses, updateStatuses, isStatusesLoading };
};
